import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  PROBLEM,
  INTUITION,
  DAG,
  CODE,
  DISPATCH,
  HONESTY,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* A small "step chip" used in the serial / parallel layouts. */
const StepChip: React.FC<{
  id: string;
  label: string;
  accent: string;
  state?: "done" | "ready" | "wait";
  on?: boolean;
}> = ({ id, label, accent, state = "wait", on = true }) => {
  const border =
    state === "done" ? COLORS.dim : state === "ready" ? accent : COLORS.line;
  const tag =
    state === "done" ? "done" : state === "ready" ? "ready" : "blocked_by ↑";
  const tagColor =
    state === "done" ? COLORS.dim : state === "ready" ? accent : COLORS.dim;
  return (
    <div
      style={{
        opacity: on ? 1 : 0.18,
        background: COLORS.panel,
        border: `1.5px solid ${border}`,
        borderRadius: 10,
        padding: "14px 18px",
        width: 250,
        boxShadow: state === "ready" ? `0 0 26px -12px ${accent}` : "none",
      }}
    >
      <div style={{ fontFamily: FONTS.mono, fontSize: 15, color: COLORS.dim }}>
        {id}
      </div>
      <div
        style={{
          fontFamily: FONTS.display,
          fontWeight: 600,
          fontSize: 24,
          color: COLORS.text,
          marginTop: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 14,
          color: tagColor,
          marginTop: 6,
        }}
      >
        {tag}
      </div>
    </div>
  );
};

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const one = interpolate(frame, [40, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="mission orchestrator · default policy" color={COLORS.cyan} />
          <Big>
            My agents run{" "}
            <span style={{ color: COLORS.amber, opacity: 0.4 + one * 0.6 }}>
              ONE STEP
            </span>{" "}
            per tick
            <br />— strictly in sequence
          </Big>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 26,
              color: COLORS.dim,
              marginTop: 34,
            }}
          >
            wall-clock = sum of every step, even the independent ones
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM: serial dispatch, B waits for A unnecessarily */
export const Scene02Problem: React.FC = () => {
  const frame = useCurrentFrame();
  // reveal the chips left→right
  const showB = frame > 60;
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the serial path · one ready step / tick" color={COLORS.amber} />
          <div style={{ display: "flex", gap: 26, alignItems: "center" }}>
            <StepChip id="step A" label="Research source A" accent="cyan" state="ready" />
            <div
              style={{
                fontFamily: FONTS.display,
                fontSize: 44,
                color: COLORS.dim,
              }}
            >
              →
            </div>
            <StepChip
              id="step B"
              label="Research source B"
              accent="amber"
              state="wait"
              on={showB}
            />
          </div>
          <div
            style={{
              fontFamily: FONTS.body,
              fontSize: 28,
              color: COLORS.amber,
              marginTop: 32,
            }}
          >
            B has no dependency on A — yet it still waits a whole poll cycle
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={PROBLEM} accent="amber" />
    </AbsoluteFill>
  );
};

/* 03 — INTUITION: a mission is a DAG, not a list */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="intuition" color={COLORS.violet} />
        <div style={{ display: "flex", gap: 40, alignItems: "center" }}>
          <Big size={64}>a LIST</Big>
          <div style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>→</div>
          <Big size={64}>
            a <span style={{ color: COLORS.violet }}>DAG</span>
          </Big>
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 28,
            color: COLORS.dim,
            marginTop: 30,
          }}
        >
          each step declares <span style={{ color: COLORS.text }}>blocked_by: []</span> — no edge ⇒ runnable together
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — DAG: the ready set (deps met) dispatches together */
const PIPE: PipeNode[] = [
  { label: "fetch", value: "step A", sub: "blocked_by: []", accent: "green" },
  { label: "fetch", value: "step B", sub: "blocked_by: []", accent: "cyan" },
  { label: "cross-validate", value: "step C", sub: "blocked_by: A,B", accent: "violet" },
  { label: "compose", value: "step D", sub: "blocked_by: C", accent: "amber" },
];
export const Scene04Dag: React.FC = () => {
  const frame = useCurrentFrame();
  const readyOn = interpolate(frame, [120, 150], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.dag.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
        <Kicker text="ready set = deps complete · not in flight" color={COLORS.green} />
        <div style={{ width: 1640 }}>
          <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={22} nodeWidth={300} />
        </div>
        <div
          style={{
            opacity: readyOn,
            fontFamily: FONTS.body,
            fontSize: 28,
            color: COLORS.green,
            marginTop: 46,
          }}
        >
          A and B are both ready at tick 1 → dispatched in the SAME tick
        </div>
      </AbsoluteFill>
      <Caption lines={DAG} accent="green" />
    </AbsoluteFill>
  );
};

/* 05 — CODE: readySteps scan + cap truncation */
const CODE_TS = `// state-machine.ts — readySteps(plan, snapshot, inFlight, maxParallel)
const cap = Number.isFinite(input.maxParallel)
  ? Math.max(1, Math.floor(input.maxParallel))   // bound the fan-out
  : 1;

for (const step of plan.steps) {
  if (snapshot.completedStepIds.has(step.id)) continue;   // skip done
  if (inFlightStepIds.has(step.id))           continue;   // skip in-flight
  const unmet = (step.blocked_by ?? []).filter(
    (dep) => !snapshot.completedStepIds.has(dep),         // deps met?
  );
  if (unmet.length === 0) ready.push(step);                // → READY
}
// nothing ready but work remains ⇒ stuck = true (deadlock, not a hang)
return { ready: ready.slice(0, cap), stuck, blocking };`;
export const Scene05Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={CODE_TS}
        language="tsx"
        filename="orchestrator/state-machine.ts · readySteps"
        highlightLines={[7, 8, 12, 15]}
        revealFrames={360}
        fontSize={22}
        width={1320}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 06 — DISPATCH: Promise.allSettled fan-out, single mission-row write */
const DISPATCH_NODES: PipeNode[] = [
  { label: "ready set", value: "[A, B]", sub: "claim each", accent: "cyan" },
  { label: "fan-out", value: "allSettled", sub: "concurrent", accent: "green" },
  { label: "reduce", value: "perStep[]", sub: "fail-isolated", accent: "violet" },
  { label: "persist", value: "1 write", sub: "single writer", accent: "amber" },
];
export const Scene06Dispatch: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.dispatch.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="dispatchReadySet · process/dispatch-ready-set.ts" color={COLORS.cyan} />
      <div style={{ width: 1640 }}>
        <PipelineGraph
          nodes={DISPATCH_NODES}
          startFrame={6}
          stepFrames={22}
          nodeWidth={300}
        />
      </div>
      <div style={{ display: "flex", gap: 26, marginTop: 46 }}>
        <Callout label="fail-isolated" accent="green" width={420}>
          One step's rejection <b style={{ color: COLORS.text }}>never loses</b> the others — Promise.allSettled
        </Callout>
        <Callout label="single-writer invariant" accent="amber" width={420}>
          Exactly <b style={{ color: COLORS.text }}>one</b> missions-row write per tick — sum tokens, advance index
        </Callout>
      </div>
    </AbsoluteFill>
    <Caption lines={DISPATCH} accent="cyan" />
  </AbsoluteFill>
);

/* 07 — HONESTY: default-OFF flag, three gates */
export const Scene07Honesty: React.FC = () => {
  const frame = useCurrentFrame();
  const g1 = interpolate(frame, [60, 90], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const g2 = interpolate(frame, [110, 140], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const g3 = interpolate(frame, [160, 190], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.honesty.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="honest status · shipped dark" color={COLORS.amber} />
        <Big size={62}>
          Built — but behind a{" "}
          <span style={{ color: COLORS.amber }}>default-OFF</span> flag
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            color: COLORS.dim,
            marginTop: 18,
          }}
        >
          unset ⇒ serial path runs (byte-identical) · the default today
        </div>
        <div style={{ display: "flex", gap: 22, marginTop: 40, alignItems: "stretch" }}>
          <div style={{ opacity: g1 }}>
            <Panel accent="cyan" title="gate 1 · flag" width={400}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.text, lineHeight: 1.7 }}>
                MISSIONS_PARALLEL_STEPS
                <div style={{ color: COLORS.dim, fontSize: 17 }}>= '1' or 'true'</div>
              </div>
            </Panel>
          </div>
          <div style={{ opacity: g2 }}>
            <Panel accent="green" title="gate 2 · cap" width={400}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.text, lineHeight: 1.7 }}>
                resolveMaxParallel &gt; 1
                <div style={{ color: COLORS.dim, fontSize: 17 }}>env default 3</div>
              </div>
            </Panel>
          </div>
          <div style={{ opacity: g3 }}>
            <Panel accent="amber" title="gate 3 · attest" width={400}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.text, lineHeight: 1.7 }}>
                MIG191_VERIFIED
                <div style={{ color: COLORS.dim, fontSize: 17 }}>= '1' (hardened claim RPC)</div>
              </div>
            </Panel>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={HONESTY} accent="amber" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={64}>
          A list runs in order.
          <br />
          A DAG runs by dependency —
          <br />
          <span style={{ color: COLORS.violet }}>the ready set fans out.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · mission orchestrator · built dark, flag-gated
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="violet" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
